import type { EngagementFieldAvailability } from '@/api/generated/model';
import {
  ENGAGEMENT_LOCATION_HELP,
  ENGAGEMENT_METRIC_COLUMNS,
  ENGAGEMENT_RATE_ICON,
  contentTypeMeta,
  contentTypeShortLabel,
  engagementColor,
} from '../lib/engagementColors';
import { ContentTypeHourHeatmap } from './ContentTypeHourHeatmap';
import { SectionLocationHint } from './EngagementChartFilters';
import { NotReadyBadge, NotReadyNote, engagementReadyState } from './EngagementNotReady';

export type EngagementTypeRow = {
  type: string;
  label: string;
  views: number;
  likes: number;
  comments: number;
  shares: number | null;
  sharesAvailability?: EngagementFieldAvailability;
  engagementRate: number | null;
  hourCounts?: number[];
};

const compact = new Intl.NumberFormat(undefined, {
  notation: 'compact',
  maximumFractionDigits: 1,
});

/**
 * Engagement-by-type table plus engaged-hour heatmap. Rendered inside Total
 * Content so both modules share one card and one page-level filter row.
 */
export function EngagementByContentTypeSection({
  rows,
  timezone,
  onTimezoneChange,
}: {
  rows: EngagementTypeRow[];
  timezone: string;
  onTimezoneChange: (timezone: string) => void;
}) {
  const RateIcon = ENGAGEMENT_RATE_ICON;
  const maxRate = rows.reduce((max, row) => Math.max(max, row.engagementRate ?? 0), 0);
  const sharesShell = rows.some(
    (row) => engagementReadyState(row.sharesAvailability) !== 'live',
  );

  return (
    <div className="space-y-4 border-t border-white/5 pt-6">
      <div>
        <div className="flex items-center gap-2">
          <h4 className="text-base font-semibold leading-6">Engagement by Content Type</h4>
          <SectionLocationHint
            text={ENGAGEMENT_LOCATION_HELP.audience}
            label={`How Engagement by Content Type uses the location filter. ${ENGAGEMENT_LOCATION_HELP.audience}`}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Rate = (likes + comments + shares) ÷ views
        </p>
      </div>
      <div className="overflow-x-auto rounded-xl border border-border/40">
        <table className="w-full min-w-[680px] border-collapse text-left text-sm">
          <thead>
            <tr className="border-b border-border/40 text-xs text-muted-foreground">
              <th className="px-4 py-3 text-center font-normal">Type</th>
              {ENGAGEMENT_METRIC_COLUMNS.map((column) => {
                const Icon = column.icon;
                return (
                  <th
                    key={column.key}
                    className="border-l border-border/40 px-4 py-3 text-center font-normal"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <Icon
                        className="size-3.5"
                        style={{ color: engagementColor(column.legend) }}
                        aria-hidden
                      />
                      {column.label}
                    </span>
                  </th>
                );
              })}
              <th className="border-l border-border/40 px-4 py-3 text-center font-normal">
                <span className="inline-flex items-center gap-1.5">
                  <RateIcon className="size-3.5 text-sky-400" aria-hidden />
                  Engagement Rate
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={ENGAGEMENT_METRIC_COLUMNS.length + 2}
                  className="px-4 py-8 text-center text-muted-foreground"
                >
                  No engagement recorded for this window.
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const { icon: Icon, color } = contentTypeMeta(row.type);
                const sharesState = engagementReadyState(row.sharesAvailability);
                const rate = row.engagementRate;
                const barWidth = maxRate > 0 && rate != null ? (rate / maxRate) * 100 : 0;

                return (
                  <tr key={row.type} className="border-b border-border/40 last:border-b-0">
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-2">
                        <span
                          className="flex size-7 shrink-0 items-center justify-center rounded-lg"
                          style={{ backgroundColor: `${color}1f` }}
                        >
                          <Icon className="size-3.5" style={{ color }} aria-hidden />
                        </span>
                        <span className="text-xs font-semibold">
                          {contentTypeShortLabel(row.type, row.label)}
                        </span>
                      </span>
                    </td>
                    {ENGAGEMENT_METRIC_COLUMNS.map((column) => {
                      const value = row[column.key];
                      const isSharesShell = column.key === 'shares' && sharesState !== 'live';
                      return (
                        <td
                          key={column.key}
                          className="border-l border-border/40 px-4 py-3 text-center text-xs font-medium tabular-nums"
                          style={{ color: engagementColor(column.legend) }}
                        >
                          <span className="inline-flex items-center gap-1.5">
                            {isSharesShell || value == null ? '—' : compact.format(value)}
                            {column.key === 'shares' ? (
                              <NotReadyBadge
                                state={sharesState}
                                hint="Shares require PostHog post_share events. There is no MySQL share ledger, so this cell stays blank until PostHog is configured."
                              />
                            ) : null}
                          </span>
                        </td>
                      );
                    })}
                    <td className="border-l border-border/40 px-4 py-3 text-center">
                      {rate == null ? (
                        <span className="text-xs text-muted-foreground">N/A</span>
                      ) : (
                        <span className="inline-block">
                          <span
                            className="block text-base font-bold tabular-nums"
                            style={{ color }}
                          >
                            {rate.toFixed(1)}%
                          </span>
                          <span className="mt-1 block h-[3px] rounded-full bg-white/10">
                            <span
                              className="block h-full rounded-full"
                              style={{ backgroundColor: color, width: `${barWidth}%` }}
                            />
                          </span>
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {sharesShell ? (
        <NotReadyNote>
          Shares require PostHog post_share events (no MySQL share ledger).
        </NotReadyNote>
      ) : null}
      <ContentTypeHourHeatmap
        slices={rows}
        title="Engagement heatmap by time of day. Brighter cells mean more engagement events for that content type in that slot."
        valueNoun="events"
        timezone={timezone}
        onTimezoneChange={onTimezoneChange}
        ariaLabel="Change engagement timezone"
      />
    </div>
  );
}
